import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertCircle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";

// Formatiert ISO-Datum als lokale Zeit + UTC-Tooltip.
export function DateTimeCell({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-overlay0">—</span>;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return <span className="text-overlay0">{iso}</span>;
  const local = new Intl.DateTimeFormat("de-CH", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
  return (
    <Tooltip content={`UTC: ${iso}`}>
      <span className="cursor-help">{local}</span>
    </Tooltip>
  );
}

interface ListPageProps {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}

export function ListPageShell({ title, description, action, children }: ListPageProps) {
  return (
    <div className="space-y-4" data-testid="resource-list">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text">{title}</h1>
          {description && <p className="text-sm text-subtext0 mt-1">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </div>
  );
}

interface ListTableShellProps {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  isEmpty: boolean;
  emptyMessage: string;
  emptyHint?: string;
  onRetry?: () => void;
  children: ReactNode;
  rowLimit?: number;
  totalRows?: number;
}

export function ListTableShell({
  isLoading,
  isError,
  errorMessage,
  isEmpty,
  emptyMessage,
  emptyHint,
  onRetry,
  children,
  rowLimit,
  totalRows,
}: ListTableShellProps) {
  // Skeleton nur wenn initial-loading UND noch nichts da
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="p-6 space-y-3" data-testid="list-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (isError) {
    return (
      <Card>
        <CardContent className="p-6 flex items-start gap-3" data-testid="list-error">
          <AlertCircle className="h-5 w-5 text-red mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-red">Fehler beim Laden</div>
            <div className="text-sm text-subtext0 mt-1">{errorMessage ?? "Unbekannter Fehler"}</div>
          </div>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} data-testid="list-retry">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  if (isEmpty) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-subtext0" data-testid="list-empty">
          <div className="text-base">{emptyMessage}</div>
          {emptyHint && <div className="text-xs mt-2 text-overlay1">{emptyHint}</div>}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        {children}
        {rowLimit && totalRows && totalRows > rowLimit && (
          <div
            className="border-t border-border p-3 text-center text-xs text-subtext0"
            data-testid="list-overflow-hint"
          >
            Zeige {rowLimit} von {totalRows}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface DetailPageShellProps {
  backTo: string;
  backLabel: string;
  title: string;
  description?: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  children: ReactNode;
}

export function DetailPageShell({
  backTo,
  backLabel,
  title,
  description,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  children,
}: DetailPageShellProps) {
  return (
    <div className="max-w-6xl space-y-4" data-testid="resource-detail">
      {/* #redesign Welle B (#9): Breadcrumb statt nacktem Zurück-Link. backLabel = Parent-Name. */}
      <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-subtext0 hover:text-text"
        >
          <ChevronLeft className="h-4 w-4" />
          {backLabel}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-overlay0" aria-hidden="true" />
        <span className="truncate font-medium text-text">{title}</span>
      </nav>

      {isLoading && (
        <Card>
          <CardContent className="p-6 space-y-3" data-testid="detail-loading">
            <Skeleton className="h-7 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      )}

      {isError && !isLoading && (
        <Card>
          <CardContent className="p-6 flex items-start gap-3" data-testid="detail-error">
            <AlertCircle className="h-5 w-5 text-red mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-red">Konnte nicht laden</div>
              <div className="text-sm text-subtext0 mt-1">{errorMessage ?? "Unbekannter Fehler"}</div>
            </div>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry} data-testid="detail-retry">
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{title}</CardTitle>
              {description && <CardDescription>{description}</CardDescription>}
            </CardHeader>
            <CardContent>{children}</CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

interface KeyValueProps {
  rows: Array<{ label: string; value: ReactNode }>;
}

export function KeyValueGrid({ rows }: KeyValueProps) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
      {rows.map((row, i) => (
        <div key={i} className="contents">
          <dt className="text-subtext0">{row.label}</dt>
          <dd className="text-text break-words">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
